import React, {useState, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Search, TrendingUp, TrendingDown, Minus, RefreshCw, ArrowRightLeft} from 'lucide-react';
import api from '../api/client';
import { metricTip } from '../lib/metric-info';
import { InfoTip } from '../components/InfoTip';

interface CurrencyInfo {
    currency: string;
    rate_eur: number;
    rate_usd: number;
    last_date: string;
}

interface ExchangeRateRow {
    date: string;
    currency_from: string;
    currency_to: string;
    rate: number;
    source: string;
    is_fallback: boolean;
}

type Tab = 'overview' | 'history';

export function Currencies() {
    const {t} = useTranslation();
    const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);
    const [rates, setRates] = useState<ExchangeRateRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [ratesLoading, setRatesLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState<Tab>('overview');
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

    useEffect(() => {
        loadCurrencies();
    }, []);

    useEffect(() => {
        if (tab === 'history') {
            loadRates();
        }
    }, [tab, dateFrom, dateTo, selectedCurrency]);

    async function loadCurrencies() {
        setLoading(true);
        try {
            const res = await api.get('/currencies');
            setCurrencies(res.data.data || []);
        } catch (e) {
            console.error('Failed to load currencies', e);
        } finally {
            setLoading(false);
        }
    }

    async function loadRates() {
        setRatesLoading(true);
        try {
            const params: Record<string, string> = {date_from: dateFrom, date_to: dateTo};
            if (selectedCurrency) params.currency = selectedCurrency;
            const res = await api.get('/currencies/rates', {params});
            setRates(res.data.data || []);
        } catch (e) {
            console.error('Failed to load rates', e);
        } finally {
            setRatesLoading(false);
        }
    }

    const filtered = useMemo(() => {
        if (!search) return currencies;
        const s = search.toUpperCase();
        return currencies.filter(c => c.currency.includes(s));
    }, [currencies, search]);

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">
                        {t('currencies.title', 'Valute & Tassi di Cambio')}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {t('currencies.subtitle', 'Valute supportate e tassi di cambio da dim_exchange_rate')}
                    </p>
                </div>
                <button
                    onClick={() => { loadCurrencies(); if (tab === 'history') loadRates(); }}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                    <RefreshCw className="h-4 w-4"/>
                    {t('common.refresh', 'Aggiorna')}
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b border-slate-200">
                <button
                    onClick={() => setTab('overview')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        tab === 'overview'
                            ? 'border-accent text-accent'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <ArrowRightLeft className="h-4 w-4 inline mr-2"/>
                    {t('currencies.tabOverview', 'Panoramica')}
                </button>
                <button
                    onClick={() => setTab('history')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        tab === 'history'
                            ? 'border-accent text-accent'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <TrendingUp className="h-4 w-4 inline mr-2"/>
                    {t('currencies.tabHistory', 'Storico Tassi')}
                </button>
            </div>

            {tab === 'overview' && (
                <OverviewTab
                    currencies={filtered}
                    loading={loading}
                    search={search}
                    onSearch={setSearch}
                    onSelectCurrency={(c) => { setSelectedCurrency(c); setTab('history'); }}
                />
            )}

            {tab === 'history' && (
                <HistoryTab
                    rates={rates}
                    loading={ratesLoading}
                    currencies={currencies}
                    selectedCurrency={selectedCurrency}
                    onSelectCurrency={setSelectedCurrency}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateFrom={setDateFrom}
                    onDateTo={setDateTo}
                />
            )}
        </div>
    );
}

function OverviewTab({currencies, loading, search, onSearch, onSelectCurrency}: {
    currencies: CurrencyInfo[];
    loading: boolean;
    search: string;
    onSearch: (s: string) => void;
    onSelectCurrency: (c: string) => void;
}) {
    const {t} = useTranslation();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full border-4 border-slate-200 border-t-accent h-10 w-10"/>
            </div>
        );
    }

    return (
        <>
            {/* Search */}
            <div className="relative mb-4 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/>
                <input
                    type="text"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder={t('currencies.searchPlaceholder', 'Cerca valuta (es. USD, BRL)...')}
                    className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
            </div>

            {/* Stats */}
            <div className="mb-4 text-sm text-slate-500">
                {currencies.length} {t('currencies.supported', 'valute supportate')}
                {currencies[0]?.last_date && (
                    <span className="ml-2">
                        &mdash; {t('currencies.lastUpdate', 'ultimo aggiornamento')}: {currencies[0].last_date}
                    </span>
                )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500">
                                <InfoTip tip={metricTip('currency')}>{t('currencies.currency', 'Valuta')}</InfoTip>
                            </th>
                            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 text-right">
                                <InfoTip tip={metricTip('rateEur')} className="justify-end">{t('currencies.rateEUR', 'Tasso vs EUR')}</InfoTip>
                            </th>
                            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 text-right">
                                {t('currencies.inverseEUR', '1 EUR =')}
                            </th>
                            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 text-right">
                                <InfoTip tip={metricTip('rateUsd')} className="justify-end">{t('currencies.rateUSD', 'Tasso vs USD')}</InfoTip>
                            </th>
                            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 text-center">
                                {t('currencies.actions', '')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {currencies.map((c) => (
                            <tr key={c.currency} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-3">
                                    <span className="font-mono font-semibold text-slate-900">{c.currency}</span>
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-700">
                                    {c.currency === 'EUR' ? '1.000000' : c.rate_eur.toFixed(6)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-500">
                                    {c.currency === 'EUR' ? '—' : (1 / c.rate_eur).toFixed(4)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-700">
                                    {c.rate_usd > 0 ? c.rate_usd.toFixed(6) : '—'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    {c.currency !== 'EUR' && (
                                        <button
                                            onClick={() => onSelectCurrency(c.currency)}
                                            className="text-xs text-accent hover:underline"
                                        >
                                            {t('currencies.viewHistory', 'Storico')}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function HistoryTab({rates, loading, currencies, selectedCurrency, onSelectCurrency, dateFrom, dateTo, onDateFrom, onDateTo}: {
    rates: ExchangeRateRow[];
    loading: boolean;
    currencies: CurrencyInfo[];
    selectedCurrency: string;
    onSelectCurrency: (c: string) => void;
    dateFrom: string;
    dateTo: string;
    onDateFrom: (d: string) => void;
    onDateTo: (d: string) => void;
}) {
    const {t} = useTranslation();

    return (
        <>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                        {t('currencies.filterCurrency', 'Valuta')}
                    </label>
                    <select
                        value={selectedCurrency}
                        onChange={(e) => onSelectCurrency(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-accent focus:outline-none"
                    >
                        <option value="">{t('currencies.allCurrencies', 'Tutte le valute')}</option>
                        {currencies.filter(c => c.currency !== 'EUR').map(c => (
                            <option key={c.currency} value={c.currency}>{c.currency}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                        {t('common.dateFrom', 'Da')}
                    </label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => onDateFrom(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-accent focus:outline-none"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                        {t('common.dateTo', 'A')}
                    </label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => onDateTo(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-accent focus:outline-none"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full border-4 border-slate-200 border-t-accent h-10 w-10"/>
                </div>
            ) : rates.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                    {t('currencies.noRates', 'Nessun tasso di cambio trovato per il periodo selezionato.')}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500">
                                    <InfoTip tip={metricTip('date')}>{t('currencies.date', 'Data')}</InfoTip>
                                </th>
                                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500">
                                    {t('currencies.from', 'Da')}
                                </th>
                                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500">
                                    {t('currencies.to', 'A')}
                                </th>
                                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 text-right">
                                    <InfoTip tip={metricTip('rate')} className="justify-end">{t('currencies.rate', 'Tasso')}</InfoTip>
                                </th>
                                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500">
                                    {t('currencies.source', 'Fonte')}
                                </th>
                                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 text-center">
                                    {t('currencies.fallback', 'Fallback')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rates.map((r, i) => (
                                <tr key={`${r.date}-${r.currency_from}-${i}`} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.date}</td>
                                    <td className="px-4 py-2 font-mono font-semibold text-slate-900">{r.currency_from}</td>
                                    <td className="px-4 py-2 font-mono text-slate-500">{r.currency_to}</td>
                                    <td className="px-4 py-2 text-right font-mono text-slate-700">{r.rate.toFixed(6)}</td>
                                    <td className="px-4 py-2 text-xs text-slate-500">{r.source}</td>
                                    <td className="px-4 py-2 text-center">
                                        {r.is_fallback ? (
                                            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
                                                fallback
                                            </span>
                                        ) : (
                                            <Minus className="h-3 w-3 text-slate-300 mx-auto"/>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-3 text-xs text-slate-400">
                {rates.length} {t('currencies.rows', 'righe')}
            </div>
        </>
    );
}
